import React, { Component, Fragment } from "react";
import { AIGenerateUI, AIGenerationProgress } from "js/editor/AIGeneration/AIComponents";
import { SearchAssets } from "js/core/utilities/searchAssets";
import Spinner from "js/react/components/Spinner";
import { AiGenerationController } from "./AiGenerationController";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import {
    FindImagesToGenerate,
    GenerateImagesForItem,
    GenerateSlideModel
} from "js/editor/AIGeneration/GenerateSlideModel";

const examples = [
    "A quote by Albert Einstein about the universe",
    "Chart the population of the world between 1900 and now",
    "Compare popular vacuum cleaners",
    "Top 3 women entrepreneurs",
    "Google's ad revenue in 2022",
    "How to write a blog post in a few easy steps",
    "Why did the chicken cross the road?",
    "Logos of the top 9 consumer companies",
    "Wordcloud about business metrics",
    "Pie chart of the market share of popular social media networks",
    "Top 5 movies of 2010 with movie posters and box office revenue",
    "Timeline of great future inventions",
    "Create a table for the last 10 years of the Superbowl including the year, the winning team, the losing team, the winning quarterback and the score",
];

export class AiGenerateSlide extends Component {
    constructor() {
        super();
        this.state = {
            generating: false,
        };
    }

    handleGenerateSlide = async prompt => {
        this.setState({ generating: "Asking DesignerBot AI to generate your slide..." });

        try {
            let slide = await AiGenerationController.GenerateSlideFromPrompt(prompt);
            slide.type = slide.type.toLowerCase();

            let images = [];
            if (slide.image && ["list", "text"].includes(slide.type)) {
                this.setState({ generating: "Finding images for your slide..." });

                let imageSearch = await new SearchAssets().searchImages(slide.image);
                images = imageSearch.results;

                if (images.length == 0) {
                    console.error("No images were returned for search: " + slide.image);
                    imageSearch = await new SearchAssets().searchImages("clouds");
                    images = imageSearch.results;
                }
            }

            this.setState({ generating: `DesignerBot AI is creating a ${slide.type} slide titled "${slide.title}"...` });
            let response = await AiGenerationController.GenerateSlideFromOutline({
                slideType: slide.type,
                userPrompt: prompt,
                slideTitle: slide.title,
            });

            // sometimes the response is returned as an array inappropriately
            if (Array.isArray(response)) {
                response = {
                    items: response
                };
            }

            this.setState({ generating: "Building your slide..." });
            let model = await GenerateSlideModel({ ...slide, ...response }, images);

            let imagesToGenerate = FindImagesToGenerate(model);

            if (imagesToGenerate.length) {
                let imagePromises = [];
                let count = imagesToGenerate.length;

                this.setState({ generating: `Finding images (${count} remaining)...` });
                for (let image of imagesToGenerate) {
                    imagePromises.push(GenerateImagesForItem(image, () => {
                        count--;
                        this.setState({ generating: `Finding images (${count} remaining)...` });
                    }));
                }
                await Promise.all(imagePromises);
            }

            this.props.onGeneratedSlide(model, {
                prompt: prompt,
                aiResponse: slide
            }
            );
        } catch (err) {
            console.error(err);
            ShowErrorDialog({
                title: "Sorry, something went wrong while generating your slide!",
                message: "Sometimes DesignerBot's AI can get confused. You can try your prompt again or edit it."
            });
            this.setState({ generating: false });
        }
    }

    render() {
        let { generating } = this.state;

        if (generating) {
            return (
                <AIGenerationProgress>
                    <Spinner />
                    <label>{generating}</label>
                </AIGenerationProgress>
            );
        } else {
            return (
                <AIGenerateUI
                    cta="SlideBot"
                    header="Ask DesignerBot AI to make you a slide."
                    description={
                        <Fragment>
                            Just ask or describe what you want your slide to be about and DesignerBot's AI will
                            automatically build you a new slide.<br />DesignerBot can create text, list, chart,
                            table, timeline, quote, team, word cloud, photo list, and logo slides.
                        </Fragment>
                    }
                    placeholder="Describe what your slide should be about. Avoid giving design instructions."
                    buttonPrompt="Generate Slide"
                    examples={examples}
                    onStartGeneration={this.handleGenerateSlide}
                />
            );
        }
    }
}

